<template>
    <div class="test-report-container" v-if="reportData">
        <div class="report-header">
            <div
                class="result-badge-large"
                :class="getResultBadgeClass(reportData.result)"
            >
                <i class="fa" :class="getResultIcon(reportData.result)"></i>
                <span>{{ reportData.result || 'N/A' }}</span>
            </div>
            <div class="test-meta">
                <div class="meta-item">
                    <i class="fa fa-clock-o"></i>
                    <span>{{ reportData.test_time || 'N/A' }}</span>
                </div>
                <div class="meta-item">
                    <span class="test-type-badge">{{
                        reportData.test_type
                    }}</span>
                </div>
            </div>
        </div>

        <div class="gauges-section">
            <div class="gauge-card">
                <div class="gauge-container">
                    <svg class="gauge" viewBox="0 0 120 120">
                        <circle class="gauge-bg" cx="60" cy="60" r="50" />
                        <circle
                            class="gauge-fill"
                            :class="getGaugeColorClass(reportData.soh)"
                            cx="60"
                            cy="60"
                            r="50"
                            :style="{
                                strokeDashoffset: calculateGaugeOffset(
                                    reportData.soh
                                ),
                            }"
                        />
                    </svg>
                    <div class="gauge-value">
                        <span class="value">{{ reportData.soh || 0 }}</span>
                        <span class="unit">%</span>
                    </div>
                </div>
                <div class="gauge-label">State of Health</div>
                <div
                    class="gauge-status"
                    :class="getHealthStatus(reportData.soh).class"
                >
                    {{ getHealthStatus(reportData.soh).text }}
                </div>
            </div>
            <div class="gauge-card">
                <div class="gauge-container">
                    <svg class="gauge" viewBox="0 0 120 120">
                        <circle class="gauge-bg" cx="60" cy="60" r="50" />
                        <circle
                            class="gauge-fill"
                            :class="getGaugeColorClass(reportData.soc)"
                            cx="60"
                            cy="60"
                            r="50"
                            :style="{
                                strokeDashoffset: calculateGaugeOffset(
                                    reportData.soc
                                ),
                            }"
                        />
                    </svg>
                    <div class="gauge-value">
                        <span class="value">{{ reportData.soc || 0 }}</span>
                        <span class="unit">%</span>
                    </div>
                </div>
                <div class="gauge-label">State of Charge</div>
                <div
                    class="gauge-status"
                    :class="getChargeStatus(reportData.soc).class"
                >
                    {{ getChargeStatus(reportData.soc).text }}
                </div>
            </div>
        </div>

        <div class="capacity-section">
            <h6 class="section-title-pro">
                <i class="fa fa-bar-chart"></i> Capacity Analysis
            </h6>
            <div class="capacity-comparison">
                <div class="capacity-bar-container">
                    <div class="capacity-labels">
                        <span>Set: {{ reportData.set_capacity }} CCA</span>
                        <span
                            >Measured:
                            {{ reportData.measured_capacity }} CCA</span
                        >
                    </div>
                    <div class="capacity-bar-bg">
                        <div
                            class="capacity-bar-fill set-capacity"
                            :style="{ width: '100%' }"
                        ></div>
                    </div>
                    <div class="capacity-bar-bg measured">
                        <div
                            class="capacity-bar-fill measured-capacity"
                            :class="
                                getPercentageStatusClass(
                                    reportData.soh,
                                    'capacity'
                                )
                            "
                            :style="{
                                width:
                                    normalizePercentage(reportData.soh) + '%',
                            }"
                        ></div>
                    </div>
                </div>
                <div class="capacity-ratio">
                    <span
                        class="ratio-value"
                        :class="
                            getPercentageStatusClass(reportData.soh, 'ratio')
                        "
                    >
                        {{ normalizePercentage(reportData.soh) }}%
                    </span>
                    <span class="ratio-label">of rated capacity</span>
                </div>
            </div>
        </div>

        <div class="details-section">
            <h6 class="section-title-pro">
                <i class="fa fa-battery-three-quarters"></i> Battery Details
            </h6>
            <div class="details-grid">
                <div class="detail-card">
                    <div class="detail-icon">
                        <i class="fa fa-tag"></i>
                    </div>
                    <div class="detail-content">
                        <span class="detail-label">Battery Type</span>
                        <span class="detail-value">
                            {{ reportData.battery_type || 'N/A' }}
                        </span>
                    </div>
                </div>
                <div class="detail-card highlight">
                    <div class="detail-icon">
                        <i class="fa fa-bolt"></i>
                    </div>
                    <div class="detail-content">
                        <span class="detail-label">Measured Voltage</span>
                        <span class="detail-value">
                            {{ reportData.measured_voltage }} V
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <div class="vehicle-section">
            <h6 class="section-title-pro">
                <i class="fa fa-car"></i> Vehicle Information
            </h6>
            <div class="vehicle-grid">
                <div class="vehicle-item">
                    <span class="vehicle-label">VIN</span>
                    <span class="vehicle-value monospace">
                        {{ reportData.vin || 'N/A' }}
                    </span>
                </div>
                <div class="vehicle-item">
                    <span class="vehicle-label">Year</span>
                    <span class="vehicle-value">{{
                        reportData.year || 'N/A'
                    }}</span>
                </div>
                <div class="vehicle-item">
                    <span class="vehicle-label">Make</span>
                    <span class="vehicle-value">{{
                        reportData.make || 'N/A'
                    }}</span>
                </div>
                <div class="vehicle-item">
                    <span class="vehicle-label">Model</span>
                    <span class="vehicle-value">{{
                        reportData.model || 'N/A'
                    }}</span>
                </div>
            </div>
        </div>

        <div class="voltage-section" v-if="hasVoltageMeasurements(reportData)">
            <h6 class="section-title-pro">
                <i class="fa fa-bolt"></i> Voltage Measurements
            </h6>
            <div class="voltage-grid-simple">
                <div
                    class="voltage-card"
                    :class="
                        getVoltageCardClass(
                            reportData.cranking_voltage,
                            'cranking'
                        )
                    "
                >
                    <span class="voltage-label">Cranking</span>
                    <div class="voltage-value-row">
                        <span class="voltage-number">
                            {{ reportData.cranking_voltage || 'N/A' }}
                        </span>
                        <span class="voltage-unit">V</span>
                    </div>
                    <span
                        class="voltage-judgment"
                        :class="
                            getVoltageJudgment(
                                reportData.cranking_voltage,
                                'cranking'
                            ).class
                        "
                    >
                        {{
                            getVoltageJudgment(
                                reportData.cranking_voltage,
                                'cranking'
                            ).text
                        }}
                    </span>
                </div>
                <div
                    class="voltage-card"
                    :class="
                        getVoltageCardClass(reportData.alt_idle_voltage, 'idle')
                    "
                >
                    <span class="voltage-label">Alt Idle</span>
                    <div class="voltage-value-row">
                        <span class="voltage-number">
                            {{ reportData.alt_idle_voltage || 'N/A' }}
                        </span>
                        <span class="voltage-unit">V</span>
                    </div>
                    <span
                        class="voltage-judgment"
                        :class="
                            getVoltageJudgment(
                                reportData.alt_idle_voltage,
                                'idle'
                            ).class
                        "
                    >
                        {{
                            getVoltageJudgment(
                                reportData.alt_idle_voltage,
                                'idle'
                            ).text
                        }}
                    </span>
                </div>
                <div
                    class="voltage-card"
                    :class="
                        getVoltageCardClass(reportData.alt_load_voltage, 'load')
                    "
                >
                    <span class="voltage-label">Alt Load</span>
                    <div class="voltage-value-row">
                        <span class="voltage-number">
                            {{ reportData.alt_load_voltage || 'N/A' }}
                        </span>
                        <span class="voltage-unit">V</span>
                    </div>
                    <span
                        class="voltage-judgment"
                        :class="
                            getVoltageJudgment(
                                reportData.alt_load_voltage,
                                'load'
                            ).class
                        "
                    >
                        {{
                            getVoltageJudgment(
                                reportData.alt_load_voltage,
                                'load'
                            ).text
                        }}
                    </span>
                </div>
                <div
                    class="voltage-card"
                    :class="
                        getVoltageCardClass(reportData.ripple_voltage, 'ripple')
                    "
                >
                    <span class="voltage-label">Ripple</span>
                    <div class="voltage-value-row">
                        <span class="voltage-number">
                            {{ reportData.ripple_voltage || 'N/A' }}
                        </span>
                        <span class="voltage-unit">V</span>
                    </div>
                    <span
                        class="voltage-judgment"
                        :class="
                            getVoltageJudgment(
                                reportData.ripple_voltage,
                                'ripple'
                            ).class
                        "
                    >
                        {{
                            getVoltageJudgment(
                                reportData.ripple_voltage,
                                'ripple'
                            ).text
                        }}
                    </span>
                </div>
            </div>
        </div>

        <div class="report-footer">
            <div class="timestamp">
                <i class="fa fa-upload"></i>
                <span
                    >Uploaded (UTC):
                    {{ formatDateTime(reportData.upload_time) }}</span
                >
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        reportData: {
            type: Object,
            default: null,
        },
        formatDateTime: {
            type: Function,
            required: true,
        },
    },
    setup() {
        const getResultBadgeClass = result => {
            if (!result) return 'badge-secondary';
            const resultUpper = result.toUpperCase();
            if (resultUpper.includes('GOOD')) return 'badge-success';
            if (resultUpper.includes('REPLACE')) return 'badge-danger';
            if (resultUpper.includes('RECHARGE')) return 'badge-warning';
            return 'badge-info';
        };

        const getResultIcon = result => {
            if (!result) return 'fa-question';
            const resultUpper = result.toUpperCase();
            if (resultUpper.includes('GOOD')) return 'fa-check-circle';
            if (resultUpper.includes('REPLACE')) return 'fa-times-circle';
            if (resultUpper.includes('RECHARGE'))
                return 'fa-exclamation-triangle';
            return 'fa-info-circle';
        };

        const calculateGaugeOffset = value => {
            const circumference = 2 * Math.PI * 50;
            const percentage = Math.min(Math.max(value || 0, 0), 100) / 100;
            return circumference * (1 - percentage);
        };

        const getGaugeColorClass = value => {
            if (value >= 80) return 'gauge-excellent';
            if (value >= 60) return 'gauge-good';
            if (value >= 40) return 'gauge-warning';
            return 'gauge-danger';
        };

        const getHealthStatus = value => {
            if (value >= 80)
                return { text: 'Excellent', class: 'status-excellent' };
            if (value >= 60) return { text: 'Good', class: 'status-good' };
            if (value >= 40) return { text: 'Fair', class: 'status-warning' };
            return { text: 'Poor', class: 'status-danger' };
        };

        const getChargeStatus = value => {
            if (value >= 80)
                return { text: 'Fully Charged', class: 'status-excellent' };
            if (value >= 50) return { text: 'Adequate', class: 'status-good' };
            if (value >= 25) return { text: 'Low', class: 'status-warning' };
            return { text: 'Critical', class: 'status-danger' };
        };

        const normalizePercentage = value => {
            const numericValue = Number(value);
            if (Number.isNaN(numericValue)) return 0;
            return Math.min(Math.max(Math.round(numericValue), 0), 100);
        };

        const getPercentageStatusClass = (value, type) => {
            const percentage = normalizePercentage(value);

            if (type === 'capacity') {
                if (percentage >= 80) return 'capacity-excellent';
                if (percentage >= 60) return 'capacity-good';
                if (percentage >= 40) return 'capacity-warning';
                return 'capacity-danger';
            }

            if (percentage >= 80) return 'ratio-excellent';
            if (percentage >= 60) return 'ratio-good';
            if (percentage >= 40) return 'ratio-warning';
            return 'ratio-danger';
        };

        const hasVoltageMeasurements = report => {
            if (!report) return false;

            return [
                report.cranking_voltage,
                report.alt_idle_voltage,
                report.alt_load_voltage,
                report.ripple_voltage,
            ].some(
                value => value !== null && value !== undefined && value !== ''
            );
        };

        const getVoltageJudgment = (voltage, type) => {
            if (!voltage && voltage !== 0)
                return { text: 'N/A', class: 'judgment-neutral' };

            if (type === 'cranking') {
                if (voltage >= 9.6)
                    return { text: 'Normal', class: 'judgment-good' };
                if (voltage >= 9.0)
                    return { text: 'Low', class: 'judgment-warning' };
                return { text: 'Weak', class: 'judgment-danger' };
            }

            if (type === 'idle' || type === 'load') {
                if (voltage >= 13.5 && voltage <= 14.5)
                    return { text: 'Normal', class: 'judgment-good' };
                if (voltage >= 13.0 && voltage <= 15.0)
                    return { text: 'Caution', class: 'judgment-warning' };
                if (voltage < 13.0)
                    return { text: 'Low', class: 'judgment-danger' };
                return { text: 'High', class: 'judgment-danger' };
            }

            if (type === 'ripple') {
                if (voltage <= 0.1)
                    return { text: 'Normal', class: 'judgment-good' };
                if (voltage <= 0.3)
                    return { text: 'Caution', class: 'judgment-warning' };
                return { text: 'Check Alt', class: 'judgment-danger' };
            }

            return { text: 'N/A', class: 'judgment-neutral' };
        };

        const getVoltageCardClass = (voltage, type) => {
            const judgment = getVoltageJudgment(voltage, type);
            if (judgment.class === 'judgment-good') return 'voltage-card-good';
            if (judgment.class === 'judgment-warning')
                return 'voltage-card-warning';
            if (judgment.class === 'judgment-danger')
                return 'voltage-card-danger';
            return '';
        };

        return {
            getResultBadgeClass,
            getResultIcon,
            calculateGaugeOffset,
            getGaugeColorClass,
            getHealthStatus,
            getChargeStatus,
            normalizePercentage,
            getPercentageStatusClass,
            hasVoltageMeasurements,
            getVoltageJudgment,
            getVoltageCardClass,
        };
    },
};
</script>

<style scoped>
.test-report-container {
    font-family: 'Montserrat', 'Helvetica Neue', Arial, sans-serif;
    padding: 0 0.5rem;
}

.report-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    background: #fff;
    border-radius: 6px;
    margin-bottom: 1.25rem;
    border: 1px solid #e3e3e3;
}

.result-badge-large {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.6rem 1.25rem;
    border-radius: 30px;
    font-size: 1rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.result-badge-large i {
    font-size: 1.2rem;
}

.result-badge-large.badge-success {
    background: rgba(65, 184, 131, 0.2);
    color: #41b883;
    border: 2px solid #41b883;
}

.result-badge-large.badge-danger {
    background: rgba(235, 94, 40, 0.2);
    color: #eb5e28;
    border: 2px solid #eb5e28;
}

.result-badge-large.badge-warning {
    background: rgba(243, 187, 69, 0.2);
    color: #f3bb45;
    border: 2px solid #f3bb45;
}

.result-badge-large.badge-info {
    background: rgba(104, 179, 200, 0.2);
    color: #68b3c8;
    border: 2px solid #68b3c8;
}

.result-badge-large.badge-secondary {
    background: rgba(164, 158, 147, 0.2);
    color: #a49e93;
    border: 2px solid #a49e93;
}

.test-meta {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.5rem;
}

.meta-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #66615b;
    font-size: 0.875rem;
}

.meta-item i {
    color: #9a9a9a;
}

.test-type-badge {
    background: rgba(122, 158, 159, 0.15);
    color: #7a9e9f;
    padding: 0.3rem 0.65rem;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.gauges-section {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.25rem;
    margin-bottom: 1.25rem;
}

.gauge-card {
    background: #fff;
    border-radius: 6px;
    padding: 1.25rem;
    text-align: center;
    box-shadow: none;
    border: 1px solid #e3e3e3;
}

.gauge-container {
    position: relative;
    width: 110px;
    height: 110px;
    margin: 0 auto 1rem;
}

.gauge {
    transform: rotate(-90deg);
    width: 100%;
    height: 100%;
}

.gauge-bg {
    fill: none;
    stroke: #e3e3e3;
    stroke-width: 10;
}

.gauge-fill {
    fill: none;
    stroke-width: 10;
    stroke-linecap: round;
    stroke-dasharray: 314.159;
    transition: stroke-dashoffset 0.8s ease-out;
}

.gauge-fill.gauge-excellent {
    stroke: #41b883;
}
.gauge-fill.gauge-good {
    stroke: #229863;
}
.gauge-fill.gauge-warning {
    stroke: #f3bb45;
}
.gauge-fill.gauge-danger {
    stroke: #eb5e28;
}

.gauge-value {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;
}

.gauge-value .value {
    font-size: 1.75rem;
    font-weight: 600;
    color: #66615b;
    line-height: 1;
}

.gauge-value .unit {
    font-size: 0.9rem;
    color: #9a9a9a;
    font-weight: 500;
}

.gauge-label {
    font-size: 0.9rem;
    font-weight: 600;
    color: #66615b;
    margin-bottom: 0.5rem;
}

.gauge-status {
    font-size: 0.75rem;
    font-weight: 600;
    padding: 0.2rem 0.6rem;
    border-radius: 20px;
    display: inline-block;
}

.status-excellent {
    background: rgba(65, 184, 131, 0.15);
    color: #41b883;
}
.status-good {
    background: rgba(34, 152, 99, 0.15);
    color: #229863;
}
.status-warning {
    background: rgba(243, 187, 69, 0.15);
    color: #bb992f;
}
.status-danger {
    background: rgba(235, 94, 40, 0.15);
    color: #eb5e28;
}

.section-title-pro {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.85rem;
    font-weight: 600;
    color: #66615b;
    margin-bottom: 0.875rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.section-title-pro i {
    color: #7a9e9f;
    font-size: 0.95rem;
}

.capacity-section {
    background: #fff;
    border-radius: 6px;
    padding: 1rem;
    margin-bottom: 1.25rem;
    box-shadow: none;
    border: 1px solid #e3e3e3;
}

.capacity-comparison {
    display: flex;
    align-items: center;
    gap: 1.25rem;
}

.capacity-bar-container {
    flex: 1;
}

.capacity-labels {
    display: flex;
    justify-content: space-between;
    font-size: 0.75rem;
    color: #9a9a9a;
    margin-bottom: 0.5rem;
    font-weight: 500;
}

.capacity-bar-bg {
    height: 10px;
    background: #e3e3e3;
    border-radius: 4px;
    overflow: hidden;
    margin-bottom: 0.5rem;
}

.capacity-bar-bg.measured {
    background: transparent;
    border: 2px dashed #a49e93;
}

.capacity-bar-fill {
    height: 100%;
    border-radius: 4px;
    transition: width 0.8s ease-out;
}

.capacity-bar-fill.set-capacity {
    background: linear-gradient(90deg, #a49e93, #9a9a9a);
}

.capacity-bar-fill.capacity-excellent {
    background: linear-gradient(90deg, #41b883, #229863);
}
.capacity-bar-fill.capacity-good {
    background: linear-gradient(90deg, #229863, #1e8449);
}
.capacity-bar-fill.capacity-warning {
    background: linear-gradient(90deg, #f3bb45, #bb992f);
}
.capacity-bar-fill.capacity-danger {
    background: linear-gradient(90deg, #eb5e28, #b33c12);
}

.capacity-ratio {
    text-align: center;
    min-width: 90px;
}

.ratio-value {
    display: block;
    font-size: 1.75rem;
    font-weight: 600;
    line-height: 1.2;
}

.ratio-label {
    font-size: 0.7rem;
    color: #9a9a9a;
}

.ratio-excellent {
    color: #41b883;
}
.ratio-good {
    color: #229863;
}
.ratio-warning {
    color: #f3bb45;
}
.ratio-danger {
    color: #eb5e28;
}

.details-section {
    margin-bottom: 1.25rem;
}

.details-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
}

.detail-card {
    display: flex;
    align-items: center;
    gap: 0.875rem;
    background: #fff;
    padding: 0.875rem;
    border-radius: 6px;
    box-shadow: none;
    border: 1px solid #e3e3e3;
}

.detail-card.highlight {
    background: #fff;
    border-color: #7a9e9f;
    border-width: 2px;
}

.detail-card.highlight .detail-icon {
    background: rgba(122, 158, 159, 0.15);
    color: #7a9e9f;
}

.detail-card.highlight .detail-label {
    color: #9a9a9a;
}

.detail-card.highlight .detail-value {
    color: #7a9e9f;
    font-weight: 700;
}

.detail-icon {
    width: 42px;
    height: 42px;
    background: rgba(122, 158, 159, 0.15);
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #7a9e9f;
    font-size: 1rem;
}

.detail-content {
    display: flex;
    flex-direction: column;
}

.detail-label {
    font-size: 0.75rem;
    color: #9a9a9a;
    font-weight: 500;
}

.detail-value {
    font-size: 1rem;
    font-weight: 600;
    color: #66615b;
}

.vehicle-section {
    background: #fff;
    border-radius: 6px;
    padding: 1rem;
    margin-bottom: 1.25rem;
    box-shadow: none;
    border: 1px solid #e3e3e3;
}

.vehicle-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 0.875rem;
}

.vehicle-item {
    text-align: center;
    padding: 0.625rem;
    background: #fff;
    border-radius: 4px;
    border: 1px solid #e3e3e3;
}

.vehicle-label {
    display: block;
    font-size: 0.7rem;
    color: #9a9a9a;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 0.2rem;
}

.vehicle-value {
    font-size: 0.875rem;
    font-weight: 600;
    color: #66615b;
}

.vehicle-value.monospace {
    font-family: 'SF Mono', 'Consolas', monospace;
    font-size: 0.8rem;
}

.voltage-section {
    background: #fff;
    border-radius: 6px;
    padding: 1rem;
    margin-bottom: 1.25rem;
    box-shadow: none;
    border: 1px solid #e3e3e3;
}

.voltage-grid-simple {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.875rem;
}

.voltage-card {
    background: #fff;
    border: 2px solid #e3e3e3;
    border-radius: 6px;
    padding: 0.875rem;
    text-align: center;
    transition: all 0.2s ease;
}

.voltage-card.voltage-card-good {
    border-color: #41b883;
    background: rgba(65, 184, 131, 0.04);
}

.voltage-card.voltage-card-warning {
    border-color: #f3bb45;
    background: rgba(243, 187, 69, 0.04);
}

.voltage-card.voltage-card-danger {
    border-color: #eb5e28;
    background: rgba(235, 94, 40, 0.04);
}

.voltage-label {
    display: block;
    font-size: 0.75rem;
    color: #9a9a9a;
    font-weight: 600;
    text-transform: uppercase;
    margin-bottom: 0.5rem;
}

.voltage-value-row {
    display: flex;
    align-items: baseline;
    justify-content: center;
    gap: 0.2rem;
    margin-bottom: 0.35rem;
}

.voltage-number {
    font-size: 1.25rem;
    font-weight: 700;
    color: #66615b;
}

.voltage-unit {
    font-size: 0.8rem;
    color: #9a9a9a;
    font-weight: 500;
}

.voltage-judgment {
    display: inline-block;
    font-size: 0.7rem;
    font-weight: 600;
    padding: 0.2rem 0.5rem;
    border-radius: 12px;
}

.judgment-good {
    background: rgba(65, 184, 131, 0.15);
    color: #41b883;
}

.judgment-warning {
    background: rgba(243, 187, 69, 0.15);
    color: #bb992f;
}

.judgment-danger {
    background: rgba(235, 94, 40, 0.15);
    color: #eb5e28;
}

.judgment-neutral {
    background: rgba(164, 158, 147, 0.15);
    color: #a49e93;
}

.report-footer {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid #e3e3e3;
}

.timestamp {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #9a9a9a;
    font-size: 0.8rem;
}

.timestamp i {
    color: #7a9e9f;
}

@media (max-width: 768px) {
    .report-header,
    .capacity-comparison {
        flex-direction: column;
        align-items: stretch;
    }

    .test-meta {
        align-items: flex-start;
    }

    .gauges-section,
    .details-grid,
    .vehicle-grid,
    .voltage-grid-simple {
        grid-template-columns: 1fr;
    }
}
</style>
